<template>
  <form
    class="h-full min-h-screen md:min-h-0 flex flex-col newsletter"
    @submit.prevent="onSubmit"
  >
    <div
      :class="themeIsDark ? 'bg-black text-white' : 'bg-white text-black'"
      class="flex-1 md:overflow-y-scroll"
    >
      <div
        class="
          heroImg-wrap
          max-h-300
          flex
          justify-center
          items-start
          w-full
          overflow-hidden
        "
      >
        <NavMenu class="z-20" />
        <img
          class="h-96 md:h-auto w-full object-cover"
          :src="themeHeaderImageUrl"
        />
      </div>
      <div
        :class="themeIsDark ? 'bg-black ' : 'bg-white'"
        class="
          relative
          content
          flex-1
          z-90
          rounded-t-xl
          md:rounded-none
          py-8
          sm:pt-12
        "
      >
        <p
          class="text-base md:text-lg text-center px-8 sm:px-44 mb-12"
          :style="$themeStyle"
          v-html="$t('newsletterTitle')"
        ></p>
        <figure class="mb-12">
          <img class="mx-auto" :src="themeNewsletterImageUrl" />
        </figure>
        <div class="px-10">
          <div class="mb-6">
            <InputText
              name="email"
              :hint="getHint(email, isEmailValid, 'email').text"
              :hint-state="getHint(email, isEmailValid, 'email').state"
              :placeholder="$t('newsletterPlaceholderEmail')"
              type="email"
              v-model="email"
            />
            <InputText
              name="person_name"
              :placeholder="$t('newsletterPlaceholderName')"
              type="text"
              v-model="name"
            />
            <InputSelect
              name="country"
              :hint="getHint(country, isCountryValid, 'country').text"
              :hint-state="getHint(country, isCountryValid, 'country').state"
              :options="countriesShown"
              :placeholder="$t('newsletterPlaceholderCountry')"
              v-model="country"
            />
          </div>
          <div class="text-xs mb-2 w-full pr-12 md:mx-auto md:w-134">
            <div class="flex mb-3" v-if="campaignIsCompetition">
              <input
                :style="{ color: themePrimaryColor }"
                :class="
                  themeIsDark
                    ? 'bg-transparent border-white'
                    : 'focus:shadow-none focus:ring-transparent focus:border-gray-300'
                "
                class="
                  w-8
                  h-8
                  rounded-md
                  focus:shadow-none
                  focus:ring-transparent
                  focus:border-transparent
                "
                type="checkbox"
                id="newsletterCompetitionTerms"
                name="newsletterCompetitionTerms"
                v-model="isCompetitionTermsChecked"
              />
              <label
                class="ml-4 text-content"
                for="newsletterCompetitionTerms"
                v-html="$t('newsletterCompetitionTerms')"
              >
              </label>
            </div>
            <div class="flex mb-3" v-if="isBothEnabled">
              <input
                :style="{ color: themePrimaryColor }"
                :class="
                  themeIsDark
                    ? 'bg-transparent border-white'
                    : 'focus:shadow-none focus:ring-transparent focus:border-gray-300'
                "
                class="
                  w-8
                  h-8
                  rounded-md
                  focus:shadow-none
                  focus:ring-transparent
                  focus:border-transparent
                "
                type="checkbox"
                id="newsletterTerms"
                name="newsletterTerms"
                v-model="isNewsletterTermsChecked"
              />
              <label
                class="ml-4 text-content"
                for="newsletterTerms"
                v-html="$t('newsletterTerms')"
              >
              </label>
            </div>
            <div
              v-else
              class="ml-12 text-content"
              v-html="$t('newsletterTerms')"
            ></div>
          </div>
        </div>
      </div>
    </div>
    <div>
      <div
        :class="themeIsDark ? 'bg-black text-white' : 'bg-white text-black'"
        class="p-10 z-10 button-wrap w-full"
      >
        <div class="mb-8">
          <Button
            type="submit"
            :title="$t('newsletterButtonNext')"
            :isFullWidth="false"
            :isDisabled="!isFormValid || loading"
          />
        </div>
        <span role="button" class="text-base text-center block" @click="goToNextStep">{{
          $t("newsletterButtonSkip")
        }}</span>
      </div>
    </div>
  </form>
</template>

<script lang="ts">
import cms from '../../cms'
import { TranslationHandler } from '../../mixins/translation-handler'
import { Component, Mixins } from 'vue-property-decorator'
import { Action, Getter } from 'vuex-class'
import { ThemeHandler } from '../../mixins/theme-handler'

@Component({
  components: {
  }
})
export default class Competition extends Mixins(TranslationHandler, ThemeHandler) {
  @Getter themeIsDark!: boolean
  @Getter themePrimaryColor!: string
  @Getter themeNewsletterImageUrl!: string
  @Getter themeHeaderImageUrl!: string

  @Getter authUserName!: string
  @Getter authEmail!: string
  @Getter authCountry!: string
  @Getter campaignIsCompetition!: boolean

  @Action('CAMPAIGN_COMPETITION_SUBSCRIBE') competionSubscribe!: (data: { name: string, email: string, country: string }) => Promise<void>
  @Action('GO_TO_NEXT_STEP') goToNextStep!: () => void
  @Action('ENSURE_STEP_NAME') ensureStepName!: (name: string) => void

  loading = false

  mounted () {
    this.ensureStepName('Newsletter')
    if (!cms.newsletter.enabled && !this.campaignIsCompetition) {
      this.goToNextStep()
    }
    if (this.authUserName) {
      this.name = this.authUserName
    }
    if (this.authEmail) {
      this.email = this.authEmail
    }
    this.country = (this.authCountry || this.$currentIpCountry) || ''
  }

  name = ''
  email = ''
  country = ''
  isCompetitionTermsChecked = false
  isNewsletterTermsChecked = false

  hints: { [k: string]: { valid: string, invalid: string } } = {
    email: {
      valid: this.$t('newsletterHintEmailValid'),
      invalid: this.$t('newsletterHintEmailInvalid'),
    },
    country: {
      valid: this.$t('newsletterHintCountryValid'),
      invalid: this.$t('newsletterHintCountryInvalid'),
    },
    name: {
      valid: this.$t('newsletterHintNameValid'),
      invalid: this.$t('newsletterHintNameInvalid'),
    }
  }

  getHint (val: string, isValid: boolean, type: string): { text: string, state: 'error' | 'default' | 'success' } {
    if (val.length === 0) {
      return { text: '', state: 'default' }
    }

    return isValid
      ? {
        text: this.hints[type].valid,
        state: 'success'
      }
      : {
        text: this.hints[type].invalid,
        state: 'error'
      }
  }

  get countriesShown (): { label: string, id: string }[] {
    return this.$tParticipatingCountriesItems.sort((a, b) => {
      if (a.id === 'DE') {
        return -1
      } else if (b.id === 'DE') {
        return 1
      }
      return 0
    })
  }

  get isNewsletterEnabled (): boolean {
    return cms.newsletter.enabled
  }

  get isBothEnabled (): boolean {
    return this.isNewsletterEnabled && this.campaignIsCompetition
  }

  get isEmailValid (): boolean {
    return this.email.length > 0 && (/\S+@\S+\.\S+/).test(this.email)
  }

  get isCountryValid (): boolean {
    return this.country.length > 0
  }

  get isTermsValid (): boolean {
    if (this.isBothEnabled) {
      return this.isNewsletterTermsChecked || this.isCompetitionTermsChecked
    }

    if (this.campaignIsCompetition) {
      return this.isCompetitionTermsChecked
    }

    return true // if newsletter is enabled no checkbxo needed
  }

  get isFormValid (): boolean {
    return [
      this.isTermsValid,
      this.isEmailValid,
      this.isCountryValid
    ].every(v => !!v)
  }

  async onSubmit () {
    if (this.isFormValid === false) { return }

    this.loading = true
    try {
      await this.onNewsletter()
      await this.onCompetition()
    } finally {
      this.loading = false
    }

    this.goToNextStep()
    // this.$router.push('/breaker/search')
  }

  async onNewsletter () {
    // Newsletter
    if (this.isBothEnabled ? this.isNewsletterTermsChecked : this.isNewsletterEnabled) {
      try {
        await cms.newsletter.signup({
          email: this.email,
          name: this.name,
          country: this.country,
        })
      } catch (e) {
        console.error(e)
        alert(e)
      }
    }
  }

  async onCompetition () {
    // Competition
    if (this.campaignIsCompetition && this.isCompetitionTermsChecked) {
      cms.tracking.trackGame('competition_subscribe_start')

      try {
        await this.competionSubscribe({
          email: this.email,
          name: this.name,
          country: this.country,
        })
        cms.tracking.trackGame('competition_subscribe_success')
      } catch (e) {
        cms.tracking.trackGame('competition_subscribe_fail')

        console.log(e)
        alert(e)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep .text-content {
  a {
    text-decoration: underline;
  }
}
</style>
